<template>
  <!-- 搜索入口按钮（放大镜图标） -->
  <div class="menu-search-trigger" @click="openPanel" :title="t('layout.menuSearch.placeholder')">
    <Icon name="Search" :size="18" />
  </div>

  <!-- 搜索面板 -->
  <teleport to="body">
    <transition name="search-fade">
      <div v-if="visible" class="menu-search-overlay" @click.self="closePanel">
        <div class="menu-search-panel">
          <!-- 搜索输入 -->
          <div class="search-input-wrapper">
            <Icon name="Search" :size="18" class="search-input-icon" />
            <input
              ref="inputRef"
              v-model="keyword"
              class="search-input"
              :placeholder="t('layout.menuSearch.placeholder')"
              @keydown.down.prevent="moveSelection(1)"
              @keydown.up.prevent="moveSelection(-1)"
              @keydown.enter.prevent="selectCurrent"
              @keydown.esc.prevent="closePanel"
            />
            <button v-if="keyword" class="search-clear" @click="keyword = ''">
              <Icon name="X" :size="14" />
            </button>
          </div>

          <!-- 搜索结果 -->
          <div class="search-results" ref="resultsRef">
            <div
              v-for="(item, index) in filteredMenus"
              :key="item.path"
              class="search-result-item"
              :class="{ 'is-active': index === selectedIndex }"
              @click="navigateTo(item)"
              @mouseenter="selectedIndex = index"
            >
              <el-icon class="result-icon">
                <component :is="getIconComponent(item.icon)" />
              </el-icon>
              <div class="result-content">
                <span class="result-title">{{ item.title }}</span>
                <span class="result-breadcrumb">{{ item.breadcrumb }}</span>
              </div>
            </div>

            <!-- 空状态 -->
            <div v-if="keyword && filteredMenus.length === 0" class="search-empty">
              <Icon name="SearchX" :size="32" />
              <p>{{ t('layout.menuSearch.noResult') }}</p>
            </div>

            <!-- 初始状态提示 -->
            <div v-if="!keyword" class="search-empty">
              <Icon name="Search" :size="32" />
              <p>{{ t('layout.menuSearch.placeholder') }}</p>
            </div>
          </div>

          <!-- 底部快捷键提示 -->
          <div class="search-footer">
            <span><kbd>↑</kbd><kbd>↓</kbd> {{ t('layout.menuSearch.navigate') }}</span>
            <span><kbd>Enter</kbd> {{ t('layout.menuSearch.confirm') }}</span>
            <span><kbd>Esc</kbd> {{ t('layout.menuSearch.close') }}</span>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { usePermissionStore } from '@/stores/permission'
import Icon from '@/components/Icon/index.vue'
import pinyinMatch from 'pinyin-match'

const { t, te } = useI18n()
const router = useRouter()
const permissionStore = usePermissionStore()

const visible = ref(false)
const keyword = ref('')
const selectedIndex = ref(0)
const inputRef = ref(null)
const resultsRef = ref(null)

// ========== 图标映射 ==========
const iconMap = {
  'House': 'Home', 'OfficeBuilding': 'Building', 'DataAnalysis': 'BarChart',
  'Grid': 'Grid3x3', 'Shop': 'ShoppingBag', 'User': 'User',
  'Document': 'FileText', 'Money': 'DollarSign', 'Promotion': 'Megaphone',
  'Bell': 'Bell', 'Ticket': 'Ticket', 'TrendCharts': 'TrendingUp',
  'Histogram': 'BarChart', 'DataBoard': 'LayoutDashboard', 'Setting': 'Settings',
  'Lock': 'Lock', 'Menu': 'Menu', 'Edit': 'Edit', 'Delete': 'Trash2',
  'Search': 'Search', 'Plus': 'Plus', 'Guide': 'Compass', 'Monitor': 'Monitor',
  'Key': 'Key', 'List': 'List', 'Finished': 'CheckCircle', 'Tickets': 'Tickets',
  'Refresh': 'RefreshCw', 'Link': 'Link', 'Tools': 'Settings',
  'wallet': 'Wallet', 'Wallet': 'Wallet', 'asset': 'Building',
  'merchant': 'ShoppingBag', 'contract': 'FileText', 'operation': 'Settings',
  'payment': 'CreditCard', 'report': 'BarChart', 'mall': 'ShoppingBag',
  'tenant': 'Building', 'marketing': 'Megaphone', 'energy': 'Zap',
  'workflow': 'GitBranch', 'system': 'Settings', 'user': 'User',
  'peoples': 'Users', 'build': 'Building', 'dashboard': 'LayoutDashboard',
}

function getIconComponent(iconName) {
  if (!iconName) return 'Menu'
  return iconMap[iconName] || iconName
}

// ========== 菜单标题 i18n ==========
function getMenuTitle(title) {
  if (!title) return ''
  const key = `menu.${title}`
  if (te(key)) return t(key)
  if (te(title)) return t(title)
  return title
}

// ========== 扁平化菜单树 ==========
const flatMenuList = computed(() => {
  const routes = permissionStore.routes || []
  const result = []

  function flatten(routes, parentTitle) {
    routes.forEach(route => {
      if (route.hidden || route.meta?.hidden) return
      if (route.meta?.menuType === 'F') return // 按钮类型不搜索

      const title = getMenuTitle(route.meta?.title)
      const path = route.meta?.fullPath || route.path

      // 叶子节点（有实际页面的）
      if (path && !route.children?.length) {
        result.push({
          title,
          path,
          icon: route.meta?.icon,
          breadcrumb: parentTitle ? `${parentTitle} / ${title}` : title
        })
      }

      // 递归子节点
      if (route.children?.length) {
        flatten(route.children, parentTitle ? `${parentTitle} / ${title}` : title)
      }
    })
  }

  flatten(routes, '')
  return result
})

// ========== 搜索匹配 ==========
const filteredMenus = computed(() => {
  if (!keyword.value.trim()) return []

  const kw = keyword.value.trim().toLowerCase()
  return flatMenuList.value.filter(item => {
    const title = item.title.toLowerCase()
    // 中文直接匹配
    if (title.includes(kw)) return true
    // 拼音匹配
    try {
      return !!pinyinMatch.match(title, kw)
    } catch {
      return false
    }
  }).slice(0, 20) // 限制最多 20 条
})

// ========== 交互逻辑 ==========
function openPanel() {
  visible.value = true
  keyword.value = ''
  selectedIndex.value = 0
  nextTick(() => {
    inputRef.value?.focus()
  })
}

function closePanel() {
  visible.value = false
  keyword.value = ''
}

function moveSelection(delta) {
  const len = filteredMenus.value.length
  if (len === 0) return
  selectedIndex.value = (selectedIndex.value + delta + len) % len
  // 滚动到可见区域
  nextTick(() => {
    const el = resultsRef.value?.children[selectedIndex.value]
    el?.scrollIntoView({ block: 'nearest' })
  })
}

function selectCurrent() {
  const item = filteredMenus.value[selectedIndex.value]
  if (item) navigateTo(item)
}

function navigateTo(item) {
  router.push(item.path)
  closePanel()
}

// 关键词变化时重置选中索引
watch(keyword, () => {
  selectedIndex.value = 0
})

// Ctrl+K 全局快捷键
function handleKeydown(e) {
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    if (visible.value) {
      closePanel()
    } else {
      openPanel()
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<script>
export default { name: 'MenuSearch' }
</script>

<style lang="scss" scoped>
/* 搜索入口按钮（纯图标） */
.menu-search-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-base);
  cursor: pointer;
  color: var(--text-secondary);
  transition: all var(--transition-base);

  &:hover {
    color: var(--text-regular);
    background: var(--bg-page);
  }
}
</style>

<!-- 全局样式（teleport 到 body，需要非 scoped） -->
<style lang="scss">
.menu-search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9999;
  display: flex;
  justify-content: center;
  padding-top: 15vh;
}

.menu-search-panel {
  width: 520px;
  max-height: 460px;
  background: var(--el-bg-overlay, #fff);
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .search-input-wrapper {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-light, #e4e7ed);
    gap: 10px;

    .search-input-icon {
      color: var(--text-secondary, #909399);
      flex-shrink: 0;
    }

    .search-input {
      flex: 1;
      border: none;
      outline: none;
      font-size: 15px;
      background: transparent;
      color: var(--text-primary, #303133);

      &::placeholder {
        color: var(--text-placeholder, #c0c4cc);
      }
    }

    .search-clear {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border: none;
      background: var(--fill-light, #f5f7fa);
      border-radius: 50%;
      cursor: pointer;
      color: var(--text-secondary, #909399);
      flex-shrink: 0;

      &:hover {
        background: var(--fill-base, #e4e7ed);
        color: var(--text-regular, #606266);
      }
    }
  }

  .search-results {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
    max-height: 340px;

    .search-result-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 12px;
      border-radius: 8px;
      cursor: pointer;
      transition: background 0.15s;

      .result-icon {
        font-size: 18px;
        color: var(--text-secondary, #909399);
        flex-shrink: 0;
      }

      .result-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;

        .result-title {
          font-size: 14px;
          color: var(--text-primary, #303133);
          font-weight: 500;
        }

        .result-breadcrumb {
          font-size: 12px;
          color: var(--text-placeholder, #c0c4cc);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      &:hover,
      &.is-active {
        background: var(--el-fill-light, #f5f7fa);
      }

      &.is-active {
        .result-icon {
          color: var(--el-color-primary, #409eff);
        }
      }
    }
  }

  .search-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: var(--text-placeholder, #c0c4cc);
    gap: 12px;

    p {
      font-size: 14px;
      margin: 0;
    }
  }

  .search-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 8px 16px;
    border-top: 1px solid var(--border-light, #e4e7ed);
    font-size: 12px;
    color: var(--text-placeholder, #c0c4cc);

    kbd {
      display: inline-block;
      padding: 1px 5px;
      border: 1px solid var(--border-light, #e4e7ed);
      border-radius: 3px;
      background: var(--fill-lighter, #f5f7fa);
      font-family: inherit;
      font-size: 11px;
      margin: 0 2px;
    }
  }
}

/* 过渡动画 */
.search-fade-enter-active,
.search-fade-leave-active {
  transition: opacity 0.2s ease;

  .menu-search-panel {
    transition: transform 0.2s ease, opacity 0.2s ease;
  }
}

.search-fade-enter-from,
.search-fade-leave-to {
  opacity: 0;

  .menu-search-panel {
    transform: scale(0.95) translateY(-10px);
    opacity: 0;
  }
}

/* 暗色主题适配 */
.dark .menu-search-panel {
  background: var(--el-bg-overlay, #1d1e1f);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6);
}
</style>
